/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useEffect, useMemo, useState } from "react";
import Card from "./Card";
import InputField from "../elements/InputField";
import DropdownField from "../elements/DropdownField";

/** Remove these if you already export them from your API types */
type SiteRecord = { url: string };
type AddSitePayload = { botId: string; url: string };
type ApiResponse<T> = { data: T | null; error: string | null };

export type BotSummary = { botId: string; name: string };

type Props = {
  bots: BotSummary[];
  loadSites: (botId: string) => Promise<ApiResponse<SiteRecord[]>>;
  addSite: (payload: AddSitePayload) => Promise<ApiResponse<SiteRecord>>;
  removeSite: (payload: AddSitePayload) => Promise<ApiResponse<SiteRecord>>;
};

function normalizeOrigin(input: string): string | null {
  const trimmed = (input || "").trim();
  if (!trimmed) return null;
  try {
    const withProto = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const u = new URL(withProto);
    return u.origin;
  } catch {
    return null;
  }
}

const labelStyle: React.CSSProperties = {
  color: "var(--text)",
  fontSize: 13,
  fontWeight: 600,
};

const chip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 10px",
  borderRadius: 999,
  background: "var(--tint, #f9f5ff)",
  border: "1px solid var(--hover, #7621b9)",
  color: "var(--text, #111827)",
  fontSize: 13,
  maxWidth: "100%",
};

const chipText: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "46ch",
};

const chipRemoveBtn: React.CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--primary)",
  cursor: "pointer",
  fontWeight: 800,
  lineHeight: 1,
  padding: 0,
  fontSize: 16,
};

const actionBtn: React.CSSProperties = {
  background: "var(--primary)",
  color: "#fff",
  border: 0,
  borderRadius: 12,
  padding: "10px 16px",
  fontWeight: 800,
  cursor: "pointer",
  fontFamily: "Montserrat, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
};

const BotSitesManager: React.FC<Props> = ({
  bots,
  loadSites,
  addSite,
  removeSite,
}) => {
  const [selectedBotId, setSelectedBotId] = useState<string>(() => bots[0]?.botId || "");
  const selectedBot = useMemo(
    () => bots.find((b) => b.botId === selectedBotId) || bots[0],
    [bots, selectedBotId]
  );

  const [urlInput, setUrlInput] = useState("");
  const [sites, setSites] = useState<SiteRecord[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<{ kind: "success" | "error"; msg: string } | null>(null);

  function clearStatusSoon(ms = 2200) {
    window.clearTimeout((clearStatusSoon as any)._t);
    (clearStatusSoon as any)._t = window.setTimeout(() => setStatus(null), ms);
  }

  const refreshSites = async (botId: string) => {
    try {
      setLoadingSites(true);
      const res = await loadSites(botId);
      if (res.error) throw new Error(res.error);
      setSites(res.data || []);
    } catch (e: any) {
      setSites([]);
      setStatus({ kind: "error", msg: e?.message || "Failed to load sites" });
      clearStatusSoon();
    } finally {
      setLoadingSites(false);
    }
  };

  useEffect(() => {
    if (selectedBotId) refreshSites(selectedBotId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBotId]);

  async function handleAdd() {
    setStatus(null);
    if (!selectedBotId) return;

    const origin = normalizeOrigin(urlInput);
    if (!origin) {
      setStatus({ kind: "error", msg: "Enter a valid URL (e.g., https://example.com)" });
      clearStatusSoon();
      return;
    }

    if (sites.some((s) => s.url === origin)) {
      setStatus({ kind: "error", msg: "That site is already added" });
      clearStatusSoon();
      return;
    }

    try {
      setAdding(true);
      const res = await addSite({ botId: selectedBotId, url: origin });
      if (res.error) throw new Error(res.error);

      setUrlInput("");
      setStatus({ kind: "success", msg: "Site added" });
      clearStatusSoon();

      await refreshSites(selectedBotId);
    } catch (e: any) {
      setStatus({ kind: "error", msg: e?.message || "Failed to add site" });
      clearStatusSoon();
    } finally {
      setAdding(false);
    }
  }

  async function handleRemove(origin: string) {
    if (!selectedBotId || !origin) return;
    try {
      setRemoving((m) => ({ ...m, [origin]: true }));
      const res = await removeSite({ botId: selectedBotId, url: origin });
      if (res.error) throw new Error(res.error);

      setStatus({ kind: "success", msg: "Site removed" });
      clearStatusSoon();

      await refreshSites(selectedBotId);
    } catch (e: any) {
      setStatus({ kind: "error", msg: e?.message || "Failed to remove site" });
      clearStatusSoon();
    } finally {
      setRemoving((m) => {
        const next = { ...m };
        delete next[origin];
        return next;
      });
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!adding && urlInput.trim()) handleAdd();
  }

  const showStatus = !!status;

  return (
    <>
      {/* Bot picker */}
      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "minmax(0, 360px)",
          maxWidth: "100%",          /* ✅ keep in-bounds */
          overflow: "hidden",         /* ✅ prevent right cut-off */
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={labelStyle}>Bot</span>
          <DropdownField
            width="auto"
            value={selectedBotId}
            onChange={(e) => setSelectedBotId(e.target.value)}
            options={bots.map((b) => ({ label: `${b.name} (${b.botId})`, value: b.botId }))}
          />
        </label>
      </div>

      {/* Add URL */}
      <form
        onSubmit={onSubmit}
        style={{
          display: "grid",
          gap: 10,
          gridTemplateColumns: "minmax(0, 1fr) auto",
          alignItems: "end",
          maxWidth: "100%",           /* ✅ keep in-bounds */
          overflow: "hidden",          /* ✅ prevent right cut-off */
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={labelStyle}>Website URL to allow</span>
          <InputField
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            text="https://example.com"
          />
        </label>

        <button
          type="submit"
          disabled={adding || !urlInput.trim()}
          style={{
            ...actionBtn,
            opacity: adding || !urlInput.trim() ? 0.6 : 1,
            cursor: adding || !urlInput.trim() ? "not-allowed" : "pointer",
            height: 42,
            whiteSpace: "nowrap",     /* ✅ avoid growing beyond container */
          }}
        >
          {adding ? "Adding..." : "Add"}
        </button>
      </form>

      {/* Status */}
      {showStatus && (
        <div
          role="status"
          aria-live="polite"
          style={{
            background: "var(--tint, #f9f5ff)",
            border: `1px solid var(--hover, #7621b9)`,
            color: "var(--text, #111827)",
            padding: "8px 12px",
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            maxWidth: "100%",         /* ✅ keep in-bounds */
            boxSizing: "border-box",
            marginBottom: 6,          /* ✅ give a little space before sites section */
          }}
        >
          {status!.msg}
        </div>
      )}

      {/* Current sites */}
      <div style={{ marginTop: showStatus ? 10 : 6 }}>
        <div style={{ ...labelStyle, marginBottom: 8 }}>Current allowed sites</div>
        <div
          className="typer-scroll-container"
          style={{
            maxHeight: 240,
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: 4,
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 10,
            background: "#fff",
            boxSizing: "border-box",  /* ✅ prevent content from pushing width */
            maxWidth: "100%",         /* ✅ keep in-bounds */
          }}
        >
          {loadingSites ? (
            <div style={{ color: "var(--text,#111827)", opacity: 0.7, fontSize: 13 }}>Loading…</div>
          ) : sites.length === 0 ? (
            <div style={{ color: "var(--text,#111827)", opacity: 0.7, fontSize: 13 }}>
              No sites yet. Add your first site above.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {sites.map((s) => (
                <span key={s.url} style={{ ...chip, opacity: removing[s.url] ? 0.5 : 1 }}>
                  <span style={chipText} title={s.url}>{s.url}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${s.url}`}
                    title="Remove"
                    style={chipRemoveBtn}
                    onClick={() => handleRemove(s.url)}
                    disabled={!!removing[s.url]}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BotSitesManager;
